import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import GoogleDriveLogo from "@/components/GoogleDriveLogo";

type DriveStatus = "loading" | "ok" | "needs_reauth" | "no_token";

const STATUS_DOT: Record<DriveStatus, string> = {
  loading: "bg-gray-400",
  ok: "bg-green-500",
  needs_reauth: "bg-red-500",
  no_token: "bg-red-500",
};

const STATUS_LABEL: Record<DriveStatus, string> = {
  loading: "Checking Drive…",
  ok: "Google Drive connected",
  needs_reauth: "Google Drive disconnected",
  no_token: "Google Drive not connected",
};

export default function DriveStatusIndicator() {
  const { user } = useAuth();
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const { data: tokenData, refetch } = trpc.gdrive.tokenStatus.useQuery(
    undefined,
    {
      enabled: !!user,
      staleTime: 5 * 60 * 1000, // re-check every 5 min
      refetchOnWindowFocus: true,
    }
  );

  const status: DriveStatus = !tokenData ? "loading" : tokenData.status === "ok" ? "ok" : tokenData.status;

  const disconnect = trpc.gdrive.disconnect.useMutation({
    onSuccess: () => {
      toast.success("Google Drive disconnected.");
      refetch();
    },
    onError: (err) => toast.error(`Disconnect failed: ${err.message}`),
  });

  const handleConnect = () => {
    if (!user) return;
    const origin = encodeURIComponent(window.location.origin);
    // Server reads the user from the session cookie — no userOpenId param needed.
    const popup = window.open(
      `/api/gdrive/auth?origin=${origin}`,
      "gdrive-auth",
      "width=520,height=620,left=200,top=100"
    );
    if (!popup) {
      toast.error("Popup blocked — please allow popups for this site.");
      return;
    }
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        refetch();
      }
    }, 800);
  };

  const handleDisconnect = () => {
    if (!user) return;
    disconnect.mutate();
    setShowDisconnectConfirm(false);
  };

  const isConnected = status === "ok";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="relative flex items-center justify-center w-9 h-9 rounded-lg hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={STATUS_LABEL[status]}
            aria-label={STATUS_LABEL[status]}
          >
            <GoogleDriveLogo className="w-5 h-5" />
            {/* Status dot */}
            <span
              className={cn(
                "absolute bottom-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-white",
                STATUS_DOT[status]
              )}
            />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-52">
          <div className="px-3 py-2">
            <p className="text-xs font-medium text-foreground">Google Drive</p>
            <p className={cn("text-xs mt-0.5", isConnected ? "text-green-600" : "text-muted-foreground")}>
              {isConnected ? "Connected" : status === "needs_reauth" ? "Token expired" : "Not connected"}
            </p>
          </div>
          <DropdownMenuSeparator />
          {isConnected ? (
            <DropdownMenuItem
              onClick={() => setShowDisconnectConfirm(true)}
              className="text-muted-foreground hover:text-destructive focus:text-destructive cursor-pointer text-sm"
            >
              Disconnect Drive
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={handleConnect}
              className="cursor-pointer text-sm"
            >
              Connect Google Drive
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Disconnect confirmation */}
      <AlertDialog open={showDisconnectConfirm} onOpenChange={setShowDisconnectConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Drive?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove your Drive connection. Uploads will no longer be sent to Google Drive until you reconnect. Files already uploaded will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-destructive hover:bg-destructive/90 text-white"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
